import { createSlice } from "@reduxjs/toolkit";
import {
  CommodityTokenTx,
  InvestPoolTx,
  Paginated,
  TreasuryAccountBalance,
} from "../../types";
import { addCommonReducers } from "../../utils/store-utils";
import {
  changeMmLatestTradesParamsAction,
  getAccountBalancesActionsAction,
  getMmLatestTradesAction,
  getMmOrderBookAction,
  getRwaContractBalancesAction,
  getTreasuryTransactionsAction,
} from "./treasury.effects";
import { RwaContractBalances } from "./types/rwa-contract-balances.type";
import { TreasuryTransactionCategory } from "./types/treasury-transactions-category.type";
import { MmOrderType, MmOrder } from "./types/mm-order.type";
import { MmTradeRecord } from "./types/mm-trade-record.type";

export const TREASURY_STATE_NAME = "treasury";

export interface TreasuryState {
  accountBalances: TreasuryAccountBalance[];
  rwaContractBalances: RwaContractBalances;
  transactions: {
    category: TreasuryTransactionCategory;
    payload: Paginated<CommodityTokenTx | InvestPoolTx>;
  };
  selectedTransactionsCategory: TreasuryTransactionCategory;
  transactionsCategoriesPageSizes: Record<TreasuryTransactionCategory, number>;
  mmOrderBook: Record<string, Record<MmOrderType, MmOrder[]>>;
  mmLatestTrades: Record<string, MmTradeRecord[]>;
  mmLatestTradesParams: Record<string, { nonMmTrade: boolean }>;
}

export const treasuryState = createSlice({
  name: TREASURY_STATE_NAME,
  initialState: {
    accountBalances: [],
    selectedTransactionsCategory: TreasuryTransactionCategory.Internal,
    transactionsCategoriesPageSizes: {
      Internal: 7,
      InvestPool: 7,
    },
    mmOrderBook: {},
    mmLatestTrades: {},
    mmLatestTradesParams: {},
  } as TreasuryState,
  reducers: {
    changeSelectedTransactionsCategory(
      state,
      { payload }: { payload: TreasuryTransactionCategory }
    ) {
      if (payload) {
        state.selectedTransactionsCategory = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountBalancesActionsAction.fulfilled, (state, action) => {
        state.accountBalances = action.payload;
      })
      .addCase(getRwaContractBalancesAction.fulfilled, (state, action) => {
        state.rwaContractBalances = action.payload;
      })
      .addCase(getTreasuryTransactionsAction.fulfilled, (state, action) => {
        state.transactions = action.payload;
      })
      .addCase(getMmOrderBookAction.fulfilled, (state, action) => {
        state.mmOrderBook[action.payload.token] = action.payload.orderBook;
      })
      .addCase(getMmLatestTradesAction.fulfilled, (state, action) => {
        state.mmLatestTrades[action.payload.token] =
          action.payload.latestTrades;
      })
      .addCase(changeMmLatestTradesParamsAction.pending, (state, action) => {
        state.mmLatestTradesParams[action.meta.arg.token] =
          action.meta.arg.params;
      });
    addCommonReducers(builder, TREASURY_STATE_NAME);
  },
});

export const treasuryActions = treasuryState.actions;
export const treasuryReducer = treasuryState.reducer;
